import { Injectable, signal } from '@angular/core';
import { supabase } from '../supabase/supabase.client';

export interface Currency {
  code: string;
  name: string;
  symbol?: string;
  decimals: number;
}

export interface UploadedImage {
  id?: string;
  file: File;
  preview: string;
  alt_text: string;
  is_primary: boolean;
  position: number;
}

export interface PropertyImage {
  id: string;
  property_id: string;
  storage_path: string;
  alt_text: string | null;
  is_primary: boolean;
  position: number;
}

export interface Property {
  id: string;
  title: string;
  description: string | null;
  price: number;
  currency: string;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  area: number | null;
  floor: number | null;
  property_type: string;
  listing_type: string;
  is_published: boolean;
  add_to_homepage: boolean;
  owner_id: string | null;
  created_at: string;
  updated_at: string;
  property_images?: PropertyImage[];
  
  // Building Information
  year_built?: number | null;
  building_standing?: string | null;
  building_floors?: number | null;
  apartments_per_floor?: number | null;
  has_elevator?: boolean;
  has_generator?: boolean;
  neighborhood?: string | null;
  zone?: string | null;
  
  // Area Information
  habitable_area_m2?: number | null;
  land_area_m2?: number | null;
  usable_area_m2?: number | null;
  total_area_m2?: number | null;
  ceiling_height_m?: number | null;
  
  // Property Features
  is_duplex?: boolean;
  has_terrace?: boolean;
  has_garden?: boolean;
  has_view?: boolean;
  is_advertised?: boolean;
  ground_floor?: boolean;
  mezzanine?: boolean;
  basement?: boolean;
  
  // Room Information
  living_room?: number | null;
  living_dining?: number | null;
  master_bedrooms?: number | null;
  guest_bathrooms?: number | null;
  maid_bathrooms?: number | null;
  maid_rooms?: number | null;
  office_rooms?: number | null;
  
  // Amenities
  anti_seismic?: boolean;
  ac_split?: boolean;
  ac_central?: boolean;
  oil_heating?: boolean;
  fireplace?: boolean;
  american_kitchen?: boolean;
  fitted_kitchen?: boolean;
  kitchenette?: boolean;
  double_glazing?: boolean;
  electric_shutters?: boolean;
  concierge?: boolean;
  videophone?: boolean;
  is_furnished?: boolean;
  
  // Condition
  general_condition?: string | null;
  needs_renovation?: boolean;
  
  // Parking & Storage
  parking_covered?: number | null;
  parking_outdoor?: number | null;
  cellar?: boolean;
  
  // Location
  google_map_url?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class PropertyService {
  imageUrls = signal<Map<string, string>>(new Map());
  private imageUrlTimestamps = new Map<string, number>();
  private readonly URL_CACHE_DURATION = 3600000; // 1 hour in milliseconds
  private readonly MAX_CACHE_SIZE = 1000; // Prevent memory leaks

  /**
   * Clear the image URL cache
   */
  clearImageCache(): void {
    this.imageUrls.set(new Map());
    this.imageUrlTimestamps.clear();
  }

  /**
   * Clear cache for a specific property's images
   */
  clearPropertyImageCache(propertyId: string): void {
    this.imageUrls.update(urls => {
      const keysToDelete: string[] = [];
      
      // Collect keys to delete
      urls.forEach((_, key) => {
        if (key.startsWith(`${propertyId}/`)) {
          keysToDelete.push(key);
        }
      });
      
      // Delete collected keys
      keysToDelete.forEach(key => {
        urls.delete(key);
        this.imageUrlTimestamps.delete(key);
      });
      
      return urls;
    });
  }

  /**
   * Check if a cached URL is still valid (not expired)
   */
  private isCacheValid(storagePath: string): boolean {
    const timestamp = this.imageUrlTimestamps.get(storagePath);
    if (!timestamp) return false;
    
    const age = Date.now() - timestamp;
    return age < this.URL_CACHE_DURATION;
  }

  /**
   * Evict oldest entries if cache exceeds max size
   */
  private evictOldestCacheEntries(): void {
    if (this.imageUrlTimestamps.size <= this.MAX_CACHE_SIZE) return;
    
    // Sort by timestamp and remove oldest 20%
    const entries = Array.from(this.imageUrlTimestamps.entries())
      .sort((a, b) => a[1] - b[1]);
    
    const entriesToRemove = Math.floor(this.MAX_CACHE_SIZE * 0.2);
    const pathsToRemove = entries.slice(0, entriesToRemove).map(e => e[0]);
    
    this.imageUrls.update(urls => {
      pathsToRemove.forEach(path => {
        urls.delete(path);
        this.imageUrlTimestamps.delete(path);
      });
      return urls;
    });
  }

  /**
   * Validates a storage path to prevent path traversal attacks.
   * Only allows alphanumeric characters, hyphens, underscores, and forward slashes.
   * @param path The storage path to validate
   * @returns true if valid, false otherwise
   */
  isValidStoragePath(path: string): boolean {
    if (!path || typeof path !== 'string') return false;
    
    // Reject paths with traversal sequences
    if (path.includes('..') || path.includes('\\') || path.includes('\0')) {
      return false;
    }
    
    // Reject paths starting with /
    if (path.startsWith('/')) {
      return false;
    }
    
    // Only allow alphanumeric, hyphens, underscores, dots, and forward slashes
    const validPathPattern = /^[a-zA-Z0-9/_.\-]+$/;
    return validPathPattern.test(path);
  }

  /**
   * Common select fields for property queries - optimized to load only necessary data
   */
  private readonly PROPERTY_SELECT_FIELDS = `
    id, title, description, price, currency,
    address_line1, address_line2, city, state, postal_code, country,
    bedrooms, bathrooms, area, property_type, listing_type,
    is_published, add_to_homepage, created_at, updated_at,
    property_images!inner (
      id,
      property_id,
      storage_path,
      alt_text,
      is_primary,
      position
    )
  ` as const;

  /**
   * Helper method to build property query with common filters
   */
  private buildPropertyQuery(additionalFilters?: { [key: string]: any }) {
    let query = supabase
      .from('properties')
      .select(this.PROPERTY_SELECT_FIELDS)
      .order('position', { foreignTable: 'property_images', ascending: true });
    
    if (additionalFilters) {
      Object.entries(additionalFilters).forEach(([key, value]) => {
        query = query.eq(key, value);
      });
    }
    
    return query;
  }

  async loadPublishedProperties(): Promise<Property[]> {
    try {
      const { data, error } = await this.buildPropertyQuery({ is_published: true })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading properties:', {
          error: error.message,
          code: error.code,
          details: error.details
        });
        throw new Error(`Failed to load properties: ${error.message}`);
      }

      return (data as Property[]) || [];
    } catch (err: any) {
      console.error('Unexpected error loading properties:', err);
      throw err;
    }
  }

  async loadRecentlyAddedProperties(): Promise<Property[]> {
    try {
      // Calculate date from 24 hours ago
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
      const isoDate = twentyFourHoursAgo.toISOString();

      const { data, error } = await this.buildPropertyQuery({ is_published: true })
        .gte('created_at', isoDate)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading recently added properties:', {
          error: error.message,
          code: error.code,
          details: error.details
        });
        throw new Error(`Failed to load recently added properties: ${error.message}`);
      }

      return (data as Property[]) || [];
    } catch (err: any) {
      console.error('Unexpected error loading recently added properties:', err);
      throw err;
    }
  }

  async loadHomepageProperties(): Promise<Property[]> {
    try {
      const { data, error } = await this.buildPropertyQuery({ 
        is_published: true,
        add_to_homepage: true 
      }).order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading homepage properties:', {
          error: error.message,
          code: error.code,
          details: error.details
        });
        throw new Error(`Failed to load homepage properties: ${error.message}`);
      }

      return (data as Property[]) || [];
    } catch (err: any) {
      console.error('Unexpected error loading homepage properties:', err);
      throw err;
    }
  }

  filterProperties(properties: Property[], searchQuery: string): Property[] {
    if (!searchQuery || searchQuery.trim() === '') return properties;
    
    const lowerQuery = searchQuery.toLowerCase().trim();
    
    return properties.filter(p => {
      // Early return on title match (most common)
      if (p.title.toLowerCase().includes(lowerQuery)) return true;
      
      // Check other fields
      const cityMatch = p.city?.toLowerCase().includes(lowerQuery);
      if (cityMatch) return true;
      
      const stateMatch = p.state?.toLowerCase().includes(lowerQuery);
      if (stateMatch) return true;
      
      const descriptionMatch = p.description?.toLowerCase().includes(lowerQuery);
      return descriptionMatch || false;
    });
  }

  formatPrice(price: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === 'USD' ? 'USD' : 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  }

  getFullAddress(property: Property): string {
    const parts = [
      property.address_line1,
      property.address_line2,
      property.city,
      property.state,
      property.postal_code,
      property.country
    ].filter(Boolean);
    return parts.join(', ');
  }

  getPrimaryImage(property: Property): PropertyImage | undefined {
    if (!property.property_images || property.property_images.length === 0) {
      return undefined;
    }
    return property.property_images.find(img => img.is_primary) || 
           property.property_images[0];
  }

  async preloadImageUrls(props: Property[]): Promise<void> {
    const now = Date.now();
    const urlsToAdd = new Map<string, string>();
    const imagesToLoad: { storagePath: string; index: number }[] = [];
    
    // First pass: collect images that need loading
    for (const prop of props) {
      const images = prop.property_images || [];
      for (const image of images) {
        // Check if we have a valid cached URL
        const cachedUrl = this.imageUrls().get(image.storage_path);
        if (cachedUrl && this.isCacheValid(image.storage_path)) {
          urlsToAdd.set(image.storage_path, cachedUrl);
          continue;
        }
        
        // Mark for loading
        imagesToLoad.push({ 
          storagePath: image.storage_path, 
          index: imagesToLoad.length 
        });
      }
    }
    
    // Parallel load all missing URLs
    if (imagesToLoad.length > 0) {
      const urlPromises = imagesToLoad.map(({ storagePath }) => 
        this.getImageUrl(storagePath)
      );
      
      const resolvedUrls = await Promise.all(urlPromises);
      
      // Add successfully loaded URLs to map
      resolvedUrls.forEach((url, index) => {
        if (url) {
          const storagePath = imagesToLoad[index].storagePath;
          urlsToAdd.set(storagePath, url);
          this.imageUrlTimestamps.set(storagePath, now);
        }
      });
    }
    
    // Update signal once with all new URLs
    if (urlsToAdd.size > 0) {
      this.imageUrls.update(existingUrls => {
        urlsToAdd.forEach((url, path) => {
          existingUrls.set(path, url);
        });
        return existingUrls;
      });
      
      // Evict old entries if needed
      this.evictOldestCacheEntries();
    }
  }

  getImageUrlFromCache(storagePath: string): string {
    const cachedUrl = this.imageUrls().get(storagePath);
    
    // If URL exists but cache is expired, remove it
    if (cachedUrl && !this.isCacheValid(storagePath)) {
      this.imageUrls.update(urls => {
        urls.delete(storagePath);
        return urls;
      });
      this.imageUrlTimestamps.delete(storagePath);
      return '';
    }
    
    return cachedUrl || '';
  }

  async getImageUrl(storagePath: string): Promise<string> {
    if (!storagePath) return '';
    
    // Validate path to prevent path traversal attacks
    if (!this.isValidStoragePath(storagePath)) {
      console.error('Invalid storage path rejected:', storagePath);
      return '';
    }
    
    try {
      // Generate a signed URL valid for 7 days (604800 seconds)
      const { data, error } = await supabase.storage
        .from('property_images')
        .createSignedUrl(storagePath, 604800);
      
      if (error) {
        console.error('Error creating signed URL:', {
          storagePath,
          error: error.message,
          code: error.name
        });
        return '';
      }
      
      if (!data?.signedUrl) {
        console.error('No signed URL returned for path:', storagePath);
        return '';
      }
      
      return data.signedUrl;
    } catch (error) {
      console.error('Exception creating signed URL:', {
        storagePath,
        error
      });
      return '';
    }
  }
}
